import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { OrganizationRole, OrganizationRoleDto, PolicyPermissionDto } from "../../interfaces/superAdmin/SuperAdmin";

import { apiGet } from "../../utilities/api-client/apiRequest";

// --- DYNAMIC ROLES ---

export async function getRoles(authToken: string, orgId: number): Promise<OrganizationRole[] | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/roles`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) return null;

        const data = await response.json();

        // Transform the DB string to a UI object
        // The C# Entity returns 'scopeConfig' as a string (JSONB)
        return data.map((role: any) => ({
            ...role,
            policies: role.policies ? role.policies.map((p: any) => ({
                ...p,
                scopeConfig: typeof p.scopeConfig === 'string' ? JSON.parse(p.scopeConfig) : p.scopeConfig
            })) : []
        }));

    } catch (error) {
        console.error("Error fetching organization roles:", error);
        return null;
    }
}

export async function upsertRole(authToken: string, orgId: number, dto: OrganizationRoleDto): Promise<OrganizationRole | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/roles`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(dto),
        });
        if (!response.ok) return null;
        return await response.json();
    } catch (error) {
        console.error("Error saving role:", error);
        return null;
    }
}

export async function deleteRole(authToken: string, orgId: number, roleId: number): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/roles/${roleId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        return response.ok;
    } catch (error) {
        console.error("Error deleting role:", error);
        return false;
    }
}

// =========================================================
// NEW: USER ASSIGNMENT METHODS
// =========================================================

export interface AssignedUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    legacyRoles: string[];
}

export async function getUsersInRole(authToken: string, orgId: number, roleId: number): Promise<AssignedUser[] | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/roles/${roleId}/users`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) return null;
        return await response.json();
    } catch (error) {
        console.error("Error fetching users in role:", error);
        return null;
    }
}

export async function assignUserToRole(
    authToken: string,
    orgId: number,
    userId: string,
    roleId: number,
    confirmRoleReplacement = false,
    expectedCurrentRoleIds: number[] = [],
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/users/assign`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ userId, roleId, confirmRoleReplacement, expectedCurrentRoleIds }),
        });
        return response.ok;
    } catch (error) {
        console.error("Error assigning user:", error);
        return false;
    }
}

export async function unassignUserFromRole(authToken: string, orgId: number, userId: string, roleId: number): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/users/unassign`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ userId, roleId }),
        });
        return response.ok;
    } catch (error) {
        console.error("Error unassigning user:", error);
        return false;
    }
}

// Helper: Fetch all employees in Org (for the dropdown)
// Note: You might need to expose this endpoint in SuperAdminController if it doesn't exist yet
export async function getEmployeesForOrg(authToken: string, orgId: number): Promise<AssignedUser[] | null> {
    try {
        // FIX: Ensure this matches the Controller Route we just added
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/${orgId}/employees`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) return [];
        return await response.json();
    } catch (error) {
        console.error("Error fetching org employees:", error);
        return [];
    }
}

// --- REFERENCE DATA ---

// export async function getSystemPermissions(authToken: string): Promise<SystemPermissionsReference | null> {
//     try {
//         const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/superadmin/policy/reference/permissions`, {
//             headers: { 'Authorization': `Bearer ${authToken}` },
//         });
//         if (!response.ok) return null;
//         return await response.json();
//     } catch (error) {
//         console.error("Error fetching system permissions:", error);
//         return null;
//     }
// }

export async function getSystemPermissions(
    authToken: string,
): Promise<ApiResponse<PolicyPermissionDto[]>> {
    let apiUrl: string = `/superadmin/policy/reference/permissions`;

    var res = apiGet<ApiResponse<PolicyPermissionDto[]>>(
        apiUrl, authToken)

    return res;
}
